import { useEffect, useState } from "react";

import dayjs, { Dayjs } from "dayjs";

/**
 * Bookings API
 *
 * Fetches the merged availability feed (Booking.com + Airbnb iCal) from the
 * Netlify function at `/api/bookings`. The wire format is:
 *
 *   GET /api/bookings -> { id: number, start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }[]
 *
 * `end` is exclusive (matches iCal VEVENT semantics) - the availability
 * calendar treats a date as unavailable when `start <= date < end`.
 */

export type Booking = {
  id: number;
  start: Dayjs;
  end: Dayjs;
};

type WireBooking = { id: number; start: string; end: string };

type BookingsState = {
  data: Booking[];
  loading: boolean;
  error: Error | null;
};

export const useBookings = (): BookingsState => {
  const [state, setState] = useState<BookingsState>({
    data: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/bookings", { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const wire = (await response.json()) as WireBooking[];
        setState({
          data: wire.map((b) => ({
            id: b.id,
            start: dayjs(b.start),
            end: dayjs(b.end),
          })),
          loading: false,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setState({
          data: [],
          loading: false,
          error:
            error instanceof Error
              ? error
              : new Error("Failed to load bookings"),
        });
      });
    return () => controller.abort();
  }, []);

  return state;
};
